<template>
    <div class="panel-board">
        <div :class="{ 'panel-board__header': true, '--skeletons': isPreloader }">
            <MySkeleton v-if="isPreloader" />
            <h1 v-else>{{ product.name }}</h1>
        </div>
        <div v-if="product?.code" class="panel-board__code panel-board__code--desktop">
            {{ $t('productCode') }} <span>{{ product?.code }}</span>
        </div>
        <div class="panel-board__content">
            <!-- INFO -->
            <div :class="{ 'panel-board__content__info': true, '--skeletons': isPreloader }">
                <div v-if="isPreloader" class="panel-board__content__info__skeletons">
                    <MySkeleton />
                    <MySkeleton />
                    <MySkeleton />
                </div>
                <template v-else>
                    <div
                        class="panel-board__content__info__content"
                        v-if="product.short_description"
                        v-html="product.short_description"
                    />
                </template>
            </div>
            <!-- MEDIA PARAMS -->
            <div v-if="isPreloader" class="panel-board__content__demo-params">
                <MySkeleton />
                <div class="panel-board__content__demo-params__list">
                    <div
                        v-for="skeleton in Array(4)"
                        :key="skeleton"
                        :class="{
                            'panel-board__content__demo-params__list__item': true,
                            '--skeleton': true
                        }"
                    >
                        <MySkeleton />
                    </div>
                </div>
            </div>
            <template v-else>
                <div
                    v-for="param in product.demo_parameters"
                    :key="param.id"
                    v-show="product.demo_parameters.length"
                    class="panel-board__content__demo-params"
                >
                    <h4>{{ param.name }}: {{ getColorActive(param) }}</h4>
                    <div class="panel-board__content__demo-params__list">
                        <button
                            v-for="item in param.values"
                            :key="item.id"
                            type="button"
                            :class="{
                                'panel-board__content__demo-params__list__item': true,
                                active: item.active,
                                disabled: !item.slug
                            }"
                            @click="setParam(item.slug)"
                        >
                            <img v-if="item.img" :src="handledImagePath(item.img)" :alt="item.name" />
                            <img
                                v-else
                                src="@/themes/novaporta/assets/images/placeholders/product-square-image-not-found.png"
                                alt="empty-param-image"
                            />
                        </button>
                    </div>
                </div>
            </template>
            <!-- SELECT PARAMS -->
            <div v-if="isPreloader" class="panel-board__content__select-params">
                <!-- ITEM -->
                <div
                    v-for="skeleton in Array(4)"
                    :key="skeleton"
                    :class="{ 'panel-board__content__select-params__item': true, '--skeleton': true }"
                >
                    <MySkeleton />
                    <MySkeleton />
                </div>
            </div>
            <div
                v-else
                v-show="product.select_parameters.length || product.extensions.length"
                class="panel-board__content__select-params"
            >
                <!-- SELECT PARAMETERS ITEMS -->

                <div v-for="param in product.select_parameters" :key="param.id" class="panel-board__content__select-params__item">
                    <div class="panel-board__content__select-params__item__header">
                        <h4>{{ param.name }}</h4>
                        <ButtonInfo
                            v-if="param.description_content"
                            @click="openModalContent({ name: param.name, description: param.description_content })"
                        />
                    </div>

                    <MySelect
                        :placeholder="$t('globalTextAttributeSelectOption')"
                        :multiselect="false"
                        :isClearable="false"
                        :optionFieldKey="'name'"
                        :theme="'gray'"
                        :options="param.values"
                        :value="activeParamValue(param.values)"
                        @input="setParam($event.slug)"
                    />
                </div>
                <!-- EXTENSIONS ITEMS -->
                <div
                    v-for="extension in product.extensions"
                    :key="extension.id"
                    class="panel-board__content__select-params__item"
                >
                    <!-- DIRECTION EXTENSION -->
                    <template v-if="extension.type === 'direction'">
                        <div class="panel-board__content__select-params__item__header">
                            <h4>{{ extension.name }}</h4>
                            <ButtonInfo
                                v-if="extension.description"
                                @click="openModalContent({ name: extension.name, description: extension.description })"
                            />
                        </div>

                        <MySelect
                            :placeholder="$t('globalTextAttributeSelectOption')"
                            :multiselect="false"
                            :isClearable="false"
                            :optionFieldKey="'name'"
                            :theme="'gray'"
                            :options="extension.values"
                            :value="direction"
                            @input="setDirection($event, extension.name)"
                        />
                    </template>
                </div>
                <h4 v-if="isHaveAdditionalExt(product.extensions)" :style="{ 'grid-column': '1'}">{{ $t('additionalOptions') }}</h4>
                <div
                    v-for="extension in product.extensions"
                    :key="extension.id"
                    class="panel-board__content__select-params__item"
                >
                    <template v-if="extension.type === 'extension'"">
                        <div class="panel-board__content__select-params__item__header">
                            <h4>{{ extension.name }}</h4>
                            <ButtonInfo
                                v-if="extension.description"
                                @click="openModalContent({ name: extension.name, description: extension.description })"
                            />
                        </div>

                        <MySelect
                            :placeholder="$t('globalTextAttributeSelectOption')"
                            :multiselect="false"
                            :isClearable="true"
                            :optionFieldKey="'name'"
                            :theme="'gray'"
                            :options="extension.values"
                            :value="activeExtensionValue(extension.id, extension.values)"
                            @input="setExtension($event, extension.id, extension.type)"
                        />
                    </template>
                </div>
            </div>
            <!-- SERVICES -->
            <div v-if="isPreloader" class="panel-board__content__services --skeletons">
                <MySkeleton />
                <div class="panel-board__content__services__inputs">
                    <!-- ITEM -->
                    <div
                        v-for="skeleton in Array(3)"
                        :key="skeleton"
                        class="panel-board__content__services__inputs__item --skeletons"
                    >
                        <MySkeleton />
                    </div>
                </div>
            </div>
            <div v-else-if="product.services.length" class="panel-board__content__services">
                <h4>{{ $t('pageProductPanelBoardSectionSevicesTitle') }}:</h4>
                <div class="panel-board__content__services__inputs">
                    <!-- ITEM -->
                    <div
                        v-for="service in product.services"
                        :key="service.id"
                        class="panel-board__content__services__inputs__item"
                    >
                        <MySwitchToggler
                            v-if="service.is_switch"
                            :labelText="service.name"
                            :disabled="false"
                            :active="service.active"
                            @input="setActiveServices(service.id, !service.active)"
                        />
                        <span v-else>{{ service.name }}</span>
                        <ButtonInfo
                            v-if="service.description"
                            @click="openModalContent({ name: service.name, description: service.description })"
                        />
                    </div>
                </div>
            </div>
            <!-- POCKET PRODUCTS -->
            <div v-if="isPreloader" class="panel-board__content__pocket-products --skeletons">
                <MySkeleton />
                <div class="panel-board__content__pocket-products__list">
                    <!-- ITEM -->
                    <div
                        v-for="skeleton in Array(4)"
                        :key="skeleton"
                        class="panel-board__content__pocket-products__list__item --skeletons"
                    >
                        <MySkeleton />
                    </div>
                </div>
            </div>
            <div v-else-if="pocketProducts.length" class="panel-board__content__pocket-products">
                <h4>{{ $t('pageProductPanelBoardSectionAdditionalProductsTitle') }}:</h4>
                <div class="panel-board__content__pocket-products__list">
                    <!-- ITEM -->
                    <div v-for="item in pocketProducts" :key="item.id" class="panel-board__content__pocket-products__list__item">
                        <div class="panel-board__content__pocket-products__list__item__img">
                            <img v-if="item.img" :src="item.img" :alt="item.name" />
                            <img
                                v-else
                                src="@/themes/novaporta/assets/images/placeholders/product-square-image-not-found.png"
                                :alt="item.name"
                            />
                        </div>
                        <h5>{{ item.name }}</h5>
                        <button :class="{ active: false }" type="button" @click="openPocketProductModal(item.slug)">
                            <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#plusIcon"></use></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes, { nullable } from 'vue-types';

import ButtonInfo from './../ButtonInfo';
import MySelect from '@/themes/novaporta/components/ui/MySelect';
import MySwitchToggler from '@/themes/novaporta/components/ui/MySwitchToggler';
import MySkeleton from '@/themes/novaporta/components/ui/MySkeleton';

import { Product, PocketProductItem } from '../../../../types';

export default {
    name: 'PanelBoard',
    props: {
        isPreloader: VueTypes.bool.isRequired,
        pocketProducts: VueTypes.arrayOf(PocketProductItem).def([]),
        product: VueTypes.oneOfType([Product, nullable]).def(null)
    },
    data() {
        return {
            activeExtensions: [],

            direction: null
        };
    },
    watch: {
        activeExtensions(newVal, oldVal) {
            this.sendActiveExtensions(newVal);
        }
    },
    components: {
        ButtonInfo,

        MySelect,
        MySwitchToggler,
        MySkeleton
    },
    methods: {
        isHaveAdditionalExt(exts) {
            return exts?.some(item => item?.type === "extension")
        },
        getColorActive(param) {
            return param.values?.find(item => item.active)?.name ? param.values?.find(item => item?.active)?.name : '';
        },
        activeParamValue(params) {
            return params.find(p => p.active === true) || null;
        },
        handledImagePath(url) {
            const baseUrl = this.$config.apiBaseUrl;

            if (url?.includes('http')) return url;
            else return `${baseUrl}${url}`;
        },

        openPocketProductModal(slug) {
            this.$emit('open-pocket-products', slug);
        },

        setActiveServices(id, flag) {
            this.$emit('set-service', { productId: this.product.id, serviceId: id, flag: flag });
        },
        openModalContent(data) {
            this.$emit('open-modal-content', {
                name: data.name,
                description: data.description
            });
        },
        setParam(slug) {
            this.$emit('set-param', slug);
        },
        sendActiveExtensions(extensions) {
            const handledExtensions = [];

            extensions.filter(ex => {
                handledExtensions.push(this.activeExtensionValue(ex.id));
            });

            this.$emit('set-active-extensions', handledExtensions);
        },
        activeExtensionValue(extensionId) {
            let activeExtensionValueId = this.activeExtensions.find(ex => ex.id === extensionId)?.valueId || null;
            const values = this.product.extensions.find(ex => ex.id === extensionId)?.values || [];

            if (activeExtensionValueId) return values.find(v => v.id === activeExtensionValueId);
            else return null;
        },
        setDirection(dir, name = '') {
            if (name) {
                dir.title = name;
            }

            this.direction = dir;
            this.$emit('set-extension-direction', dir);
        },
        setExtension(value, extensionId) {
            const valueId = value?.id;
            const extensionMock = {
                id: extensionId,
                valueId: valueId
            };

            if (!valueId) {
                this.activeExtensions = this.activeExtensions.filter(ex => ex.id !== extensionId);
            } else if (this.activeExtensions.find(ex => ex.id === extensionId)) {
                this.activeExtensions = [...this.activeExtensions.filter(ex => ex.id !== extensionId), extensionMock];
            } else {
                this.activeExtensions = [...this.activeExtensions, extensionMock];
            }
        }
    },
    mounted() {
        if (this.activeExtensions.length) this.sendActiveExtensions(this.activeExtensions);
    }
};
</script>

<style lang="scss">
@import '~/themes/novaporta/assets/styles/utils.scss';
@import './styles.scss';
</style>
